// StatSig feature flags — shared types, enums, and gate definitions.

export type DateString = `${number}-${number}-${number}`;

export type TeamConfig = {
  NAME: string; // The team name responsible for the feature (usually a Slack channel)
  CHANNEL: string; // Link to the team channel (usually Slack URL)
};

/** Fields shared by both gates and experiments. */
export interface BaseFeatureConfig {
  NAME: string; // The display name.
  LINK: string; // A link to the config (usually in Statsig).
  TEAM: TeamConfig;
  DESCRIPTION: string; // Keep it short and concise!
  EXPIRATION: DateString; // Planned removal date (YYYY-MM-DD).
  EVENTS?: Partial<Record<keyof typeof EXP_EVENTS, string>>; // Optional loggable events.
}

export interface GateConfig extends BaseFeatureConfig {
  GATE: string; // The StatSig gate id.
}

// You can fire events from withing the Tiger Cloud Console. This is a list of
// comments that are used to log events for the experiments.
export enum EXP_EVENTS {
  SERVICE_CREATED = "service_created",
  SERVICE_DELETED = "service_deleted",
  SERVICE_RESIZED = "service_resized",
  DOWNLOAD_CONFIG = "download_config",
  COPY_CONNECTION_STRING = "copy_connection_string",
  COPY_TIGER_CLI_COMMAND = "copy_tiger_cli_command",
  CLICK_CONNECT_CLI_SETUP = "click_connect_cli_setup",
  TEXT_CREATE_TABLE_ERROR = "text_create_table_error",
  TEXT_UPLOAD_ERROR = "text_upload_error",
  TEXT_PROCESSING_ERROR = "text_processing_error",
  CLICK_CONNECTION_TILE = "click_connection_tile",
  CLICK_SCHEDULE_SESSION = "click_schedule_session",
  OPEN_SCHEDULETIME_MODAL = "open_scheduletime_modal",
  CLICK_SERVICE_TYPE_TILE = "click_service_type_tile",
  ONBOARDING_SELECT_PLAN = "onboarding_select_plan",
  ONBOARDING_CLICKED_SEE_OTHER_PLANS = "onboarding_clicked_see_other_plans",
  SHOW_PLAN_RECOMMENDATION = "show_plan_recommendation",
  PLAN_CHANGE = "plan_change",
  PLAN_CHANGE_API_CALL = "plan_change_api_call",
  CLICK_CLOUD_SIGN_UP = "click_cloud_sign_up",
  HYPERTABLE_WIZARD_STARTED = "hypertable_wizard_started",
  HYPERTABLE_WIZARD_FINISHED = "hypertable_wizard_finished",
  HYPERTABLE_WIZARD_ERROR = "hypertable_wizard_error",
  CLICK_CONNECT_OPTION = "click_connect_option",
  CLICK_CLI_ICON = "click_cli_icon",
}

// Sometimes we just need to fire a generic event not related to an experiment.
export enum SYSTEM_EVENTS {
  PROJECT_CREATED = "project_created",
  PAYMENT_METHOD_ADDED = "payment_method_added",
  CLICK_DATA_MODE = "click_data_mode",
  SUPPORT_TICKET_CREATED = "support_ticket_created",
}

export const TEAMS = {
  TEAM_DATA_ONBOARDING: {
    NAME: "team-data-onboarding",
    CHANNEL: "https://app.slack.com/client/T02FBQQDT/C04M7NQFH89",
  },
  TEAM_SELF_SERVE: {
    NAME: "team-self-serve",
    CHANNEL: "https://app.slack.com/client/T02FBQQDT/C05UYLDMC58",
  },
  TEAM_NEW_USER_EXPERIENCE: {
    NAME: "team-new-user-experience",
    CHANNEL: "https://app.slack.com/client/T02FBQQDT/C05JA3QHSLQ",
  },
  TEAM_PRODUCTION_EXPERIENCE: {
    NAME: "team-production-experience",
    CHANNEL: "https://app.slack.com/client/T02FBQQDT/C04N23EJETA",
  },
  TEAM_VECTOR: {
    NAME: "team-vector",
    CHANNEL: "https://app.slack.com/client/T02FBQQDT/C05CAEE7EUD",
  },
  TEAM_CORE_UI: {
    NAME: "core-ui",
    CHANNEL: "https://app.slack.com/client/T02FBQQDT/C065B2S8E6S",
  },
  TEAM_SALES_OPS_ENTERPRISE: {
    NAME: "sales-opps-enterprise",
    CHANNEL: "https://app.slack.com/client/T02FBQQDT/C06GCEEH406",
  },
  TEAM_FRONTEND: {
    NAME: "team-frontend",
    CHANNEL: "https://app.slack.com/client/T02FBQQDT/C032ZV5UKU2",
  },
  TEAM_PROJECT_TRANSIT_GATEWAY: {
    NAME: "project-transit-gateway",
    CHANNEL: "https://app.slack.com/client/T02FBQQDT/C07G6K5UYTE",
  },
  TEAM_PLATFORM: {
    NAME: "team-platform",
    CHANNEL: "https://app.slack.com/client/T02FBQQDT/C02L64ZQJ0P",
  },
  TEAM_BUSINESS_USER_EXPERIENCE: {
    NAME: "team-business-user-experience",
    CHANNEL: "https://app.slack.com/client/T02FBQQDT/C04M9HW2N5T",
  },
} satisfies Record<string, TeamConfig>;

// A template for creating new experiments.
//
// export const FEAT_TEMPLATE: GateConfig = {
//   GATE: "",
//   NAME: "",
//   LINK: "",
//   TEAM: TEAMS.,
//   DESCRIPTION: '',
//   EXPIRATION: "YYYY-MM-DD",
// };

export const FEAT_INTERNAL_INSIGHTS: GateConfig = {
  GATE: "internal_insights_interval_options",
  NAME: "Internal Insights Interval Options",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/internal_insights_interval_options",
  TEAM: TEAMS.TEAM_PRODUCTION_EXPERIENCE,
  DESCRIPTION:
    "Adds additional interval options to the Insights page (@robatticus).",
  EXPIRATION: "2030-01-01",
};

export const FEAT_SCHEDULETIME: GateConfig = {
  GATE: "feat_scheduletime",
  NAME: "Schedule Time Modal",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_scheduletime",
  TEAM: TEAMS.TEAM_SALES_OPS_ENTERPRISE,
  DESCRIPTION:
    "Triggers a modal to schedule a meeting with a Tiger Cloud Sales member.",
  EXPIRATION: "2026-01-01",
};

export const FEAT_SKIP_REGION_VALIDATION: GateConfig = {
  GATE: "feat_skip_region_validation",
  NAME: "Skip validation for region selection",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_skip_region_validation",
  TEAM: TEAMS.TEAM_FRONTEND,
  DESCRIPTION:
    "WARNING: This shows all products in all regions, regardless if regions are present or complete. Missing regions in products will cause the UI to be unstable until the API catches up.",
  EXPIRATION: "2030-01-01",
};

export const FEAT_AWS_MARKETPLACE: GateConfig = {
  GATE: "aws_marketplace",
  NAME: "AWS Marketplace",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/aws_marketplace",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  DESCRIPTION: "The project is created via AWS Marketplace.",
  EXPIRATION: "2025-05-01",
};

export const FEAT_TRANSIT_GATEWAY: GateConfig = {
  GATE: "feat_transit_gateway",
  NAME: "Transit Gateway Feature",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_transit_gateway",
  TEAM: TEAMS.TEAM_PROJECT_TRANSIT_GATEWAY,
  DESCRIPTION:
    "Transit Gateway Feature that adds new fields at vpc peering to connect with a TGW",
  EXPIRATION: "2025-01-01",
};

export const FEAT_ICEBERG: GateConfig = {
  GATE: "feat_iceberg",
  NAME: "Iceberg Connector",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_iceberg",
  TEAM: TEAMS.TEAM_DATA_ONBOARDING,
  DESCRIPTION: "Adds a Iceberg connector to the connectors tab.",
  EXPIRATION: "2026-01-01",
};

export const FEAT_ICEBERG_CREATE: GateConfig = {
  GATE: "feat_iceberg_create",
  NAME: "Iceberg Connector- Create",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_iceberg_create",
  TEAM: TEAMS.TEAM_DATA_ONBOARDING,
  DESCRIPTION: "Expose the create iceberg connector page.",
  EXPIRATION: "2026-01-01",
};

export const FEAT_KAFKA_CONNECTOR: GateConfig = {
  GATE: "feat_kafka_connector",
  NAME: "Kafka Connector",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_kafka_connector",
  TEAM: TEAMS.TEAM_DATA_ONBOARDING,
  DESCRIPTION: "Adds a Kafka connector to the connectors tab.",
  EXPIRATION: "2026-01-01",
};

export const FEAT_DESTINATION_INTEREST: GateConfig = {
  GATE: "feat_destination_interest",
  NAME: "Destination connector interest",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_destination_interest",
  TEAM: TEAMS.TEAM_DATA_ONBOARDING,
  DESCRIPTION:
    'Adds Snowflake and Databricks "Notify me" tiles to the destination connectors section.',
  EXPIRATION: "2027-04-01",
};

export const FEAT_CREDIT_CARD_PRE_VERIFY: GateConfig = {
  GATE: "feat_credit_card_pre_verify",
  NAME: "Credit Card Pre-Verification",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_credit_card_pre_verify",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  DESCRIPTION:
    "Pre-verify credit cards before adding them as a payment method.",
  EXPIRATION: "2026-02-01",
};

export const FEAT_AZURE_MARKETPLACE: GateConfig = {
  GATE: "feat_azure_marketplace",
  NAME: "Azure Marketplace Integration",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_azure_marketplace",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  DESCRIPTION: "Shows the Azure Provider in the console",
  EXPIRATION: "2027-01-01",
};

export const FEAT_SUPPORT_CASES: GateConfig = {
  GATE: "feat_support_cases",
  NAME: "Support Cases",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_support_cases",
  TEAM: TEAMS.TEAM_PRODUCTION_EXPERIENCE,
  DESCRIPTION:
    "Enables the support cases UI allowing users to view, reply to, and close support cases.",
  EXPIRATION: "2027-01-01",
};

export const FEAT_SQL_AUTOCOMPLETE: GateConfig = {
  GATE: "feat_sql_autocomplete",
  NAME: "SQL Autocomplete",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_sql_autocomplete",
  TEAM: TEAMS.TEAM_PRODUCTION_EXPERIENCE,
  DESCRIPTION:
    "Enables SQL autocomplete suggestions in the expandable SQL editor.",
  EXPIRATION: "2027-01-01",
};

export const FEAT_ENHANCED_STORAGE: GateConfig = {
  GATE: "feat_enhanced_storage",
  NAME: "Enhanced Storage",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_enhanced_storage",
  TEAM: TEAMS.TEAM_PLATFORM,
  DESCRIPTION:
    "Controls visibility of the Enhanced storage option in the storage type selector.",
  EXPIRATION: "2027-01-01",
};

export const FEAT_COMMAND_PALETTE_RECENTS: GateConfig = {
  GATE: "feat_command_palette_recents",
  NAME: "Command Palette Recents",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_command_palette_recents",
  TEAM: TEAMS.TEAM_FRONTEND,
  DESCRIPTION: "Enables recently used commands in the command palette.",
  EXPIRATION: "2026-10-01",
};

export const FEAT_NEW_EXPLORER_HYPERTABLE: GateConfig = {
  GATE: "feat_new_explorer_hypertable",
  NAME: "New Explorer Hypertable",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_new_explorer_hypertable",
  TEAM: TEAMS.TEAM_PRODUCTION_EXPERIENCE,
  DESCRIPTION:
    "Enables the new explorer hypertable card and auto-tune view in the table info page.",
  EXPIRATION: "2027-05-01",
};

export const FEAT_HYPERTABLE_CHUNKS_INTERVALS_CARD: GateConfig = {
  GATE: "feat_hypertable_chunks_intervals_card",
  NAME: "Hypertable Chunks Intervals Card",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_hypertable_chunks_intervals_card",
  TEAM: TEAMS.TEAM_PRODUCTION_EXPERIENCE,
  DESCRIPTION:
    "Enables the redesigned chunks intervals (highlight) card on the hypertable info page.",
  EXPIRATION: "2027-05-01",
};

export const FEAT_ACTIVE_USER_SESSIONS: GateConfig = {
  GATE: "feat_active_user_sessions",
  NAME: "Active User Sessions",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_active_user_sessions",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  DESCRIPTION:
    "Shows the Active Sessions card on account settings (list/revoke sessions).",
  EXPIRATION: "2027-01-31",
};

export const FEAT_FILE_IMPORT_REDESIGN: GateConfig = {
  GATE: "feat_file_import_redesign",
  NAME: "File Import Redesign",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_file_import_redesign",
  TEAM: TEAMS.TEAM_DATA_ONBOARDING,
  DESCRIPTION:
    "Redesign the file import page to be more user-friendly and intuitive.",
  EXPIRATION: "2027-01-01",
};

export const FEAT_PROJECT_DELETION: GateConfig = {
  GATE: "feat_project_deletion",
  NAME: "Project Deletion",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_project_deletion",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  DESCRIPTION:
    "Enables the Delete project box on project settings, the Delete account box on account settings, and the deletion flows.",
  EXPIRATION: "2027-03-01",
};

export const FEAT_GCP: GateConfig = {
  GATE: "feat_gcp",
  NAME: "GCP Cloud Provider",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_gcp",
  TEAM: TEAMS.TEAM_PLATFORM,
  DESCRIPTION:
    "Shows the GCP cloud provider and its regions (gcp-*) in the console.",
  EXPIRATION: "2027-09-01",
};

export const FEAT_EU_WEST_3: GateConfig = {
  GATE: "feat_eu_west_3",
  NAME: "AWS eu-west-3 Region",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_eu_west_3",
  TEAM: TEAMS.TEAM_PLATFORM,
  DESCRIPTION: "Shows the eu-west-3 (Europe/Paris) region in the app.",
  EXPIRATION: "2027-10-15",
};

export const FEAT_PG_UPGRADE_WITH_REPLICAS: GateConfig = {
  GATE: "feat_pg_upgrade_with_replicas",
  NAME: "PostgreSQL upgrades with replicas",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_pg_upgrade_with_replicas",
  TEAM: TEAMS.TEAM_PLATFORM,
  DESCRIPTION: "Allow PostgreSQL major upgrades with HA and read replicas.",
  EXPIRATION: "2026-10-08",
};

export const FEAT_ICEBERG_SYNC_ADVANCED_OPTIONS: GateConfig = {
  GATE: "feat_iceberg_sync_advanced_options",
  NAME: "Iceberg Sync Advanced Options",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_iceberg_sync_advanced_options",
  TEAM: TEAMS.TEAM_DATA_ONBOARDING,
  DESCRIPTION:
    "Adds format version and tiered data options when syncing tables to Iceberg. Depends on timescaledb-lake 0.7.2.",
  EXPIRATION: "2027-03-01",
};

export const FEAT_TIGERLAKE_OBSERVABILITY: GateConfig = {
  GATE: "feat_tigerlake_observability",
  NAME: "Iceberg Metrics",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_tigerlake_observability",
  TEAM: TEAMS.TEAM_DATA_ONBOARDING,
  DESCRIPTION:
    "Displays the TigerLake observability metrics on the iceberg connector detail page.",
  EXPIRATION: "2027-01-01",
};

export const FEAT_STORAGE_BREAKDOWN: GateConfig = {
  GATE: "feat_storage_breakdown",
  NAME: "Storage Breakdown",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_storage_breakdown",
  TEAM: TEAMS.TEAM_PRODUCTION_EXPERIENCE,
  DESCRIPTION:
    "Shows the WAL, temp and other storage categories with a categories drawer on the service overview.",
  EXPIRATION: "2027-03-01",
};

export const FEAT_HIDE_DATA_VIEW: GateConfig = {
  GATE: "hide_data_view",
  NAME: "Hide Data View",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/hide_data_view",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  DESCRIPTION:
    "Hides the Ops view / Data view toggle and does not load the PopSQL iframe.",
  EXPIRATION: "2027-09-01",
};

export const FEAT_LOGS_FIELDS_SIDEBAR: GateConfig = {
  GATE: "feat_logs_fields_sidebar",
  NAME: "Logs fields sidebar",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_logs_fields_sidebar",
  TEAM: TEAMS.TEAM_PRODUCTION_EXPERIENCE,
  DESCRIPTION:
    "Opens a side panel with the structured (JSON log) fields of a log entry on the Logs page.",
  EXPIRATION: "2027-03-31",
};

export const FEAT_FLUID_STORAGE: GateConfig = {
  GATE: "feat_fluid_storage",
  NAME: "Fluid storage",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_fluid_storage",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  DESCRIPTION: "Adds fluid storage services to the console",
  EXPIRATION: "2027-01-01",
};

/**
 * Shows branches of Fluid Services: create, list, start, stop, resize, rename,
 * and delete. Branches are in preview, so keep this gate internal.
 */
export const FEAT_BRANCHES: GateConfig = {
  GATE: "feat_branches",
  NAME: "Branches",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/gates/feat_branches",
  TEAM: TEAMS.TEAM_PLATFORM,
  DESCRIPTION: "Shows branches of Fluid Services in the console.",
  EXPIRATION: "2027-04-30",
};

// Add to the list to show for the ExperimentsModal.
export const ALL_GATES: GateConfig[] = [
  FEAT_INTERNAL_INSIGHTS,
  FEAT_SCHEDULETIME,
  FEAT_SKIP_REGION_VALIDATION,
  FEAT_AWS_MARKETPLACE,
  FEAT_TRANSIT_GATEWAY,
  FEAT_ICEBERG,
  FEAT_ICEBERG_CREATE,
  FEAT_KAFKA_CONNECTOR,
  FEAT_DESTINATION_INTEREST,
  FEAT_CREDIT_CARD_PRE_VERIFY,
  FEAT_AZURE_MARKETPLACE,
  FEAT_SUPPORT_CASES,
  FEAT_ENHANCED_STORAGE,
  FEAT_SQL_AUTOCOMPLETE,
  FEAT_COMMAND_PALETTE_RECENTS,
  FEAT_NEW_EXPLORER_HYPERTABLE,
  FEAT_HYPERTABLE_CHUNKS_INTERVALS_CARD,
  FEAT_ACTIVE_USER_SESSIONS,
  FEAT_FILE_IMPORT_REDESIGN,
  FEAT_PROJECT_DELETION,
  FEAT_GCP,
  FEAT_EU_WEST_3,
  FEAT_PG_UPGRADE_WITH_REPLICAS,
  FEAT_ICEBERG_SYNC_ADVANCED_OPTIONS,
  FEAT_TIGERLAKE_OBSERVABILITY,
  FEAT_STORAGE_BREAKDOWN,
  FEAT_HIDE_DATA_VIEW,
  FEAT_LOGS_FIELDS_SIDEBAR,
  FEAT_FLUID_STORAGE,
  FEAT_BRANCHES,
];
